import * as THREE from 'three';
import { createFishGeometry, FISH_MODELS } from './fishGeometry.js';

export function createFishMaterial({ modelId = 'clownfish', animatedUniforms = [] } = {}) {
  const model = Object.hasOwn(FISH_MODELS, modelId) ? FISH_MODELS[modelId] : null;
  if (!model) throw new Error(`Unknown fish model: ${modelId}`);
  const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .54, metalness: 0,
    // Keep the clear-water draw pass, but opaque fragments write depth so the
    // nearer body occludes another instance instead of blending through it.
    transparent: true, opacity: 1, depthWrite: true });
  material.userData.surfaceSchool = true;
  material.userData.fishModelId = modelId;
  const creatureTime = { value: 0 };
  animatedUniforms.push(creatureTime);
  material.onBeforeCompile = shader => {
    shader.uniforms.uCreatureTime = creatureTime;
    shader.uniforms.uTailStart = { value: model.tailStart };
    shader.uniforms.uTailAmplitude = { value: model.tailAmplitude };
    shader.uniforms.uTailFrequency = { value: model.tailFrequency };
    shader.vertexShader = shader.vertexShader.replace('#include <common>',
      '#include <common>\nuniform float uCreatureTime;\nuniform float uTailStart;\nuniform float uTailAmplitude;\nuniform float uTailFrequency;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        float fishPhase=0.;
        #ifdef USE_INSTANCING
          fishPhase=dot(instanceMatrix[3].xz,vec2(.71,.43));
        #endif
        float tailWeight=1.-smoothstep(uTailStart,uTailStart+.28,position.x);
        transformed.z+=sin(uCreatureTime*uTailFrequency+position.x*3.8+fishPhase)*uTailAmplitude*tailWeight;`);
  };
  material.customProgramCacheKey = () => `authored-fish-v2:${modelId}`;
  return material;
}

const geometryCache = new Map();

export function fishPickRadius(geometry, scale = 1) {
  if (!geometry.boundingSphere) geometry.computeBoundingSphere();
  const sphere = geometry.boundingSphere;
  // The eight-sided pick proxy is inscribed inside an ideal sphere. Enclose
  // that faceting too, then keep a small aiming margin for a swimming tail.
  const extent = sphere.radius + sphere.center.length() + (geometry.userData.tailAmplitude || 0);
  return Math.max(.78, extent * scale * 1.13 + .16);
}

// All anatomical pieces are baked into one mesh: one simulation and one draw
// per school. Materials stay local so outside islands retain their own clock.
export function createFishSource(modelId, { animatedUniforms = [] } = {}) {
  if (!geometryCache.has(modelId)) geometryCache.set(modelId, createFishGeometry(modelId));
  geometryCache.get(modelId).userData.tailAmplitude = FISH_MODELS[modelId].tailAmplitude;
  const source = new THREE.Mesh(geometryCache.get(modelId), createFishMaterial({ modelId, animatedUniforms }));
  source.name = `Fish_${modelId}_Source`;
  source.userData.fishModelId = modelId;
  source.userData.authoredFish = true;
  return source;
}
